import React, { useState, useCallback } from 'react';
import { tv } from 'tailwind-variants';
import validateErrorSvg from '../../assets/svg/validateError.svg';
import inputMask, { MaskTypes } from '../../helpers/Masks/';
import Typography from '../Typography';

const inputVariants = tv({
  slots: {
    control: [
      'w-full text-sm rounded-lg px-4 h-12',
      'placeholder:text-neutral-dark [&:not(:placeholder-shown)]:text-neutral-darkest',
      'transition-colors appearance-none outline-none border',
    ],
  },
  variants: {
    invalid: {
      false: {
        control:
          'bg-neutral-lightest border-neutral-light focus:border-primary focus:shadow-[0_4px_4px_rgba(33,33,33,0.12)] caret-neutral-darkest',
      },
      true: {
        control: 'bg-error-lightest border-error-medium caret-error-medium pr-10',
      },
    },
  },
  defaultVariants: { invalid: false },
});

interface Props {
  label?: string;
  ariaLabel?: string;
  id?: string;
  labelId?: string;
  placeholder?: string;
  type: string;
  readOnly?: boolean;
  disabled?: boolean;
  title?: string;
  maxLength?: number;
  value?: string;
  name: string;
  className?: string;
  onChange?: (e: any) => void;
  children?: React.ReactNode;
  maskType?: MaskTypes;
  rules?: Array<Function>;
  loginPassword?: boolean;
  showErrorIcon?: boolean;
}

const Input = ({
  label,
  ariaLabel,
  id,
  labelId,
  placeholder,
  type,
  readOnly,
  disabled,
  title,
  maxLength,
  value,
  name,
  className,
  onChange,
  children,
  maskType,
  rules,
  loginPassword,
  showErrorIcon = true,
}: Props) => {
  const [errors, setErrors] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [required, setRequired] = useState(false);

  const { control } = inputVariants({ invalid });

  const handleChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (maskType) {
        const mask = inputMask[maskType];
        event.currentTarget.value = mask(event);
      }
      if (typeof onChange === 'function') {
        onChange(event);
      }
    },
    [maskType, onChange]
  );

  const validate = (val: any) => {
    const errorList: string[] = [];
    let isInvalid = false;

    setRequired(false);
    setErrors('');

    rules?.map((fn) => {
      const valid = fn(val);
      if (valid === true) return;
      if (valid.includes('obrigatório')) setRequired(true);
      errorList.push(valid);
    });

    if (errorList.length > 0) {
      setErrors(errorList.join(' '));
      isInvalid = true;
    }

    setInvalid(isInvalid);

    if (typeof onChange === 'function') {
      onChange({ isValid: errorList.length === 0, ...val });
    }
  };

  const inputId = id ?? name;

  return (
    <div className="w-full" onSubmit={validate}>
      {label && (
        <label
          id={labelId}
          className="flex justify-between text-xs text-neutral-dark ml-4 mb-2.5"
          htmlFor={inputId}
        >
          {label}{' '}
          {loginPassword && (
            <a
              href="/forgot-password"
              className="text-xs font-normal max-[768px]:font-bold text-support2-medium"
            >
              Esqueci a minha senha
            </a>
          )}
        </label>
      )}
      <div className="relative">
        <input
          className={control({ className })}
          id={inputId}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          title={title}
          required={required}
          maxLength={maxLength}
          value={value}
          name={name}
          onChange={handleChangeInput}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          onBlur={validate}
        />
        {invalid && showErrorIcon ? (
          <img
            src={validateErrorSvg}
            alt=""
            aria-hidden="true"
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none size-[18px]"
          />
        ) : (
          children
        )}
      </div>
      {invalid && errors && (
        <Typography as="p" variant="caption" className="text-error-medium ml-4 mt-1">
          {errors}
        </Typography>
      )}
    </div>
  );
};

export default Input;
